const API_URL = import.meta.env.VITE_API_URL;

interface StreamedData {
  chunk?: string;
  status?: string;
  [key: string]: unknown;
}

export const postStreaming = async (
  endpoint: string,
  body: Record<string, unknown>,
  onChunk?: (chunk: string) => void,
  onComplete?: () => void,
  onError?: (error: Error) => void
): Promise<void> => {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  let readerInstance: ReadableStreamDefaultReader<Uint8Array> | null = null;

  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      let errorDetail = 'Unknown HTTP error';
      try {
        const errorBody = await response.json();
        if (typeof errorBody === 'object' && errorBody !== null && (errorBody as Record<string, unknown>).message) {
          errorDetail = (errorBody as Record<string, string>).message;
        } else {
          errorDetail = JSON.stringify(errorBody);
        }
      } catch {
        try {
          errorDetail = await response.text();
        } catch {
          errorDetail = response.statusText;
        }
      }
      throw new Error(`HTTP error ${response.status}: ${errorDetail}`);
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    readerInstance = reader;
    const decoder = new TextDecoder();
    let buffer = '';

    let reading = true;
    while (reading) {
      try {
        const { done, value } = await reader.read();
        if (done) {
          reading = false;
          if (buffer.trim().length > 0) {
            console.warn('Stream ended with unprocessed buffer:', buffer);
          }
          if (onComplete) onComplete();
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        let eolIndex: number;
        while ((eolIndex = buffer.indexOf('\n')) >= 0) {
          const line = buffer.substring(0, eolIndex).trim();
          buffer = buffer.substring(eolIndex + 1);

          if (line.startsWith('data:')) {
            const jsonString = line.substring(5).trim();
            if (jsonString) {
              try {
                const parsedData: StreamedData = JSON.parse(jsonString);
                if (parsedData.chunk) {
                  if (onChunk) onChunk(parsedData.chunk);
                }
                if (parsedData.status === 'done') {
                  console.log('Received done status from stream.');
                  reading = false;
                  if (onComplete) onComplete();
                  if (readerInstance) {
                    await readerInstance.cancel('Stream processing complete due to done status');
                    readerInstance = null;
                  }
                  break;
                }
              } catch (parseError) {
                console.error('Error parsing streamed JSON line:', jsonString, parseError);
              }
            }
          } else if (line) {
            console.log('SSE non-data line:', line);
          }
        }
        if (!reading) break;

      } catch (streamError) {
        reading = false;
        console.error('Error reading stream:', streamError);
        if (onError) onError(streamError as Error);
        if (readerInstance) {
            await readerInstance.cancel('Error during stream reading');
            readerInstance = null;
        }
        break;
      }
    }
  } catch (error) {
    console.error('Streaming API call failed:', error);
    if (onError) onError(error as Error);
  } finally {
    if (readerInstance) {
        try {
            await readerInstance.cancel('Stream processing finished or failed.');
        } catch (cancelError) {
            console.error('Error cancelling reader:', cancelError);
        }
    }
  }
};

const streamingApiService = {
  postStreaming,
};

export default streamingApiService;
